import { supabase } from '@/integrations/supabase/client';
import { getPlayerStats } from './pointsService';
import { slugify } from '@/lib/slugify';
import { getSteamStatsFromDB } from './steamSyncService';

export interface PublicPlayerProfile {
    id: string;
    display_name: string;
    avatar_url: string | null;
    bio: string | null;
    user_type: string;
    created_at: string;
    player_border: string | null;
    country: string | null;
    // Social media
    twitch_url: string | null;
    youtube_url: string | null;
    tiktok_url: string | null;
    instagram_url: string | null;
    twitter_url: string | null;
    bluesky_url: string | null;
    steam_url: string | null;
    // Stats
    coins: number;
    level: string;
    badges: string[];
    player_level: number;
    total_playtime: number;
    games_tested: number;
    // Counts
    favorites_count: number;
    purchased_games_count: number;
    achievement_progress: number;
    missions_count: number;
}

/**
 * Fetch public player profile data by display name slug
 * @param displayNameSlug - The player's display name as a URL slug (e.g., "indie-keys")
 * @returns Public player profile data or null if not found
 */
export async function getPublicPlayerProfile(displayNameSlug: string): Promise<PublicPlayerProfile | null> {
    try {
        // Fetch all player profiles with active border
        const { data: profiles, error: profilesError } = await supabase
            .from('profiles')
            .select('id, display_name, avatar_url, bio, user_type, created_at, active_border_id, country, twitch_url, youtube_url, tiktok_url, instagram_url, twitter_url, bluesky_url, steam_url, shop_items(metadata)')
            .eq('user_type', 'player');

        if (profilesError || !profiles) {
            console.error('Error fetching profiles:', profilesError);
            return null;
        }

        // Find profile by matching slugified display name
        const profile = profiles.find(p => slugify(p.display_name) === displayNameSlug.toLowerCase());

        if (!profile) {
            return null;
        }

        // Extract player border from shop_items metadata
        const playerBorder = profile.shop_items?.metadata?.style || null;

        // Fetch player stats
        const stats = await getPlayerStats(profile.id);
        if (!stats) {
            return null;
        }

        // Fetch favorites count
        const { data: favoritesData, error: favoritesError } = await supabase
            .from('game_favorites')
            .select('id', { count: 'exact' })
            .eq('player_id', profile.id);

        const favoritesCount = favoritesData?.length || 0;

        // Fetch purchased games count
        const { data: purchasedData, error: purchasedError } = await supabase
            .from('purchased_keys')
            .select('id', { count: 'exact' })
            .eq('player_id', profile.id);

        const purchasedGamesCount = purchasedData?.length || 0;

        // Fetch achievement stats
        const steamStats = await getSteamStatsFromDB(profile.id);
        let totalAchievements = 0;
        let unlockedAchievements = 0;

        steamStats.forEach(stat => {
            if (stat.total_achievements > 0) {
                totalAchievements += stat.total_achievements;
                unlockedAchievements += stat.unlocked_achievements;
            }
        });

        const achievementProgress = totalAchievements > 0
            ? Math.round((unlockedAchievements / totalAchievements) * 100)
            : 0;

        // Fetch missions count
        const { data: missionsData, error: missionsError } = await supabase
            .from('player_missions')
            .select('id', { count: 'exact' })
            .eq('player_id', profile.id);

        const missionsCount = missionsData?.length || 0;

        return {
            id: profile.id,
            display_name: profile.display_name,
            avatar_url: profile.avatar_url,
            bio: profile.bio,
            user_type: profile.user_type,
            created_at: profile.created_at,
            player_border: playerBorder,
            country: profile.country,
            twitch_url: profile.twitch_url,
            youtube_url: profile.youtube_url,
            tiktok_url: profile.tiktok_url,
            instagram_url: profile.instagram_url,
            twitter_url: profile.twitter_url,
            bluesky_url: profile.bluesky_url,
            steam_url: profile.steam_url,
            coins: stats.coins,
            level: stats.level,
            badges: stats.badges,
            player_level: stats.player_level,
            total_playtime: stats.total_playtime,
            games_tested: stats.games_tested,
            favorites_count: favoritesCount,
            purchased_games_count: purchasedGamesCount,
            achievement_progress: achievementProgress,
            missions_count: missionsCount,
        };
    } catch (error) {
        console.error('Error fetching public player profile:', error);
        return null;
    }
}

/**
 * Fetch player's favorite games for public display
 * @param playerId - The player's ID
 * @returns Array of favorite games with basic info
 */
export async function getPublicPlayerFavorites(playerId: string) {
    try {
        const { data, error } = await supabase
            .from('game_favorites')
            .select(`
        id,
        created_at,
        game:games(
          id,
          title,
          cover_image_url,
          developer:profiles(display_name, studio_name, is_premium, premium_end_date)
        )
      `)
            .eq('player_id', playerId)
            .order('created_at', { ascending: false })
            .limit(12);

        if (error) {
            console.error('Error fetching public favorites:', error);
            return [];
        }

        return data || [];
    } catch (error) {
        console.error('Error fetching public favorites:', error);
        return [];
    }
}
